import { useEffect, useState } from 'react'
import { CrimeFilters, CRIME_TYPES } from '../lib/types'

interface SidebarProps {
  isOpen: boolean
  onToggle: () => void
  filters: CrimeFilters
  onCrimeTypeToggle: (crimeType: string) => void
  onClearFilters: () => void
}

type CrimeDataset = {
  lastUpdated: string
  period: { start: string; end: string }
  national: { crimes: Record<string, number> }
}

export default function Sidebar({
  isOpen,
  onToggle,
  filters,
  onCrimeTypeToggle,
  onClearFilters,
}: SidebarProps) {
  if (!isOpen) return null

  const [crimeCounts, setCrimeCounts] = useState<Record<string, number>>({})
  const [lastUpdated, setLastUpdated] = useState<string | null>(null)
  const [period, setPeriod] = useState<{ start: string; end: string } | null>(null)

  useEffect(() => {
    const loadCounts = async () => {
      try {
        const res = await fetch('/data/crime-data.json')
        if (!res.ok) return
        const data = (await res.json()) as CrimeDataset
        setCrimeCounts(data.national?.crimes || {})
        setLastUpdated(data.lastUpdated || null)
        setPeriod(data.period || null)
      } catch (err) {
        console.error('Failed to load crime counts:', err)
      }
    }
    loadCounts()
  }, [])

  const activeCount = Object.values(filters.crimeTypes).filter(Boolean).length
  const totalCount = Object.keys(filters.crimeTypes).length

  return (
    <>
      {/* Mobile backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-20 md:hidden"
        onClick={onToggle}
      />
      <aside className="fixed md:relative inset-y-0 left-0 z-30 w-72 bg-[var(--color-bg-sidebar)] border-r border-[var(--color-border)] flex flex-col overflow-hidden md:z-auto">
      {/* Header */}
      <div className="p-4 border-b border-[var(--color-border)] flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-[var(--color-text)]">Filtros</h2>
          <p className="text-xs text-[var(--color-text-muted)]">
            {activeCount}/{totalCount} tipos de delito activos
          </p>
        </div>
        <button
          onClick={onToggle}
          className="text-[var(--color-text-muted)] hover:text-[var(--color-text)] p-1"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Crime Types */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-[var(--color-text)]">Tipos de Delito</h3>
            <button
              onClick={onClearFilters}
              className="text-xs text-[var(--color-primary)] hover:underline"
            >
              {activeCount === totalCount ? 'Quitar todos' : 'Seleccionar todos'}
            </button>
          </div>

          <div className="space-y-2">
            {Object.entries(CRIME_TYPES).map(([key, crime]) => {
              const isActive = filters.crimeTypes[key as keyof typeof filters.crimeTypes]
              const count = crimeCounts[key] || 0

              return (
                <label
                  key={key}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-[var(--color-bg-card)] cursor-pointer transition-colors"
                >
                  <div
                    onClick={(e) => {
                      e.preventDefault()
                      onCrimeTypeToggle(key)
                    }}
                    className={`toggle-switch ${isActive ? 'active' : ''}`}
                    style={isActive ? { background: crime.color } : undefined}
                  />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-[var(--color-text)] block truncate">
                      {crime.nameEs}
                    </span>
                  </div>
                  <span className="text-xs text-[var(--color-primary)] font-medium">
                    {count.toLocaleString()}
                  </span>
                </label>
              )
            })}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-[var(--color-border)] bg-[var(--color-bg-dark)]">
        <p className="text-xs text-[var(--color-text-muted)] text-center">
          Datos: SESNSP / INEGI
          <br />
          {period ? `Período: ${period.start} → ${period.end}` : 'Período: —'}
          <br />
          {lastUpdated
            ? `Actualizado: ${new Date(lastUpdated).toLocaleDateString('es-MX')}`
            : 'Actualizado: —'}
        </p>
      </div>
    </aside>
    </>
  )
}
